// Adapter: turn the raw Bluebox IoT snapshot (state.aplicacao.velocimetro
// [thingId]) into the normalized FftSpectrum contract the chart consumes.
//
// The data SOURCE is unchanged — the same AppSync/MQTT fields the device has
// always sent: fftX/fftY/fftZ (magnitude arrays, sometimes JSON-stringified
// inside an envelope), BinNum (bin count), BinFstep (Hz per bin) and ts. This
// just reshapes them and derives the fields the device does not send
// (sampleRate, unit, rpm), so real and simulated spectra share one renderer.

/**
 * @typedef {import('../types/fft-spectrum').FftSpectrum} FftSpectrum
 */

// The Bluebox accelerometer reports vibration amplitude in mg (milli-g), so
// the FFT spectrum is in mg unless a caller overrides it. (The device payload
// carries no unit field; the cadastro label for AccX/Y/Z was historically
// m/s2 but the firmware sends mg.)
export const DEFAULT_FFT_UNIT = 'mg';
// Blades are not in telemetry; this default enables the BPF label and is
// overridable per machine when the backend starts exposing it.
export const DEFAULT_BLADE_COUNT = 5;

const parseJson = (value) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
};

// Locate the FFT block for one axis inside the (possibly nested / stringified)
// IoT envelope. Mirrors the original extractFftPayload so the reading path is
// byte-for-byte the same, just centralized.
const extractAxisBlock = (raw, axisUpper) => {
  const envelope = parseJson(raw);
  if (!envelope || typeof envelope !== 'object') return null;

  const candidates = [
    envelope,
    parseJson(envelope.d),
    parseJson(envelope.fft),
    parseJson(envelope.FFT),
    parseJson(envelope.fftData),
  ].filter(Boolean);
  const field = `fft${axisUpper}`;
  const block = candidates.find((item) => Array.isArray(parseJson(item[field])));
  if (!block) return null;

  const values = parseJson(block[field]).map(Number).filter(Number.isFinite);
  const binNum = Number(block.BinNum);
  const binFstep = Number(block.BinFstep);
  if (!values.length || !Number.isFinite(binFstep) || binFstep <= 0) return null;

  return {
    values:
      Number.isInteger(binNum) && binNum > 0 && binNum <= values.length
        ? values.slice(0, binNum)
        : values,
    binFstep,
    timestamp: envelope.ts || block.ts || null,
  };
};

const toIso = (value) => {
  if (!value) return new Date().toISOString();
  // Accept epoch seconds, epoch ms or an existing ISO/date string.
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    const ms = Math.abs(numeric) < 1e12 ? numeric * 1000 : numeric;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toISOString();
};

/**
 * Normalize one axis of an IoT snapshot into an FftSpectrum.
 * @param {*} iotData       state.aplicacao.velocimetro[thingId]
 * @param {('x'|'y'|'z'|'X'|'Y'|'Z')} axis
 * @param {Object} [opts]
 * @param {string} [opts.unit]
 * @param {number} [opts.bladeCount]
 * @returns {FftSpectrum|null} null when the axis has no FFT data yet.
 */
export const parseSpectrumFromIot = (iotData, axis, opts = {}) => {
  // The snapshot is normally an object, but the device sometimes wraps the
  // payload as a JSON string — accept both (extractAxisBlock parses it).
  if (iotData == null || (typeof iotData !== 'object' && typeof iotData !== 'string')) {
    return null;
  }
  const axisUpper = String(axis).toUpperCase();
  const axisLower = String(axis).toLowerCase();
  const block = extractAxisBlock(iotData, axisUpper);
  if (!block) return null;

  const magnitudes = block.values;
  const binCount = magnitudes.length;
  const binStep = block.binFstep;
  // Nyquist = binCount * binStep, so sampleRate = 2 * binCount * binStep.
  const sampleRate = 2 * binCount * binStep;

  // rpm is not in the FFT block; reuse the shaft speed from the same snapshot
  // (Rev is the Bluebox short code, Rotacao the long one). Only an object
  // snapshot carries it — a string envelope simply yields no rpm.
  const source = typeof iotData === 'object' ? iotData : {};
  const rpmRaw = Number(
    source.Rev !== undefined ? source.Rev : source.Rotacao
  );
  const bladeCount =
    opts.bladeCount !== undefined ? opts.bladeCount : DEFAULT_BLADE_COUNT;

  return {
    axis: axisLower,
    sampleRate,
    binCount,
    binStep,
    unit: opts.unit || DEFAULT_FFT_UNIT,
    timestamp: toIso(block.timestamp),
    magnitudes,
    ...(Number.isFinite(rpmRaw) && rpmRaw > 0 ? { rpm: rpmRaw } : {}),
    ...(Number.isFinite(bladeCount) && bladeCount > 0 ? { bladeCount } : {}),
  };
};

/**
 * Parse all three axes from one snapshot. Missing axes come back as null.
 * @param {*} iotData
 * @param {Object} [opts]
 * @returns {{ x: FftSpectrum|null, y: FftSpectrum|null, z: FftSpectrum|null }}
 */
export const parseSpectrumSetFromIot = (iotData, opts = {}) => ({
  x: parseSpectrumFromIot(iotData, 'x', opts),
  y: parseSpectrumFromIot(iotData, 'y', opts),
  z: parseSpectrumFromIot(iotData, 'z', opts),
});
